import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { X, Building, Globe, Users, User, Phone, Mail, Linkedin, Calendar, Clock, MessageCircle, Briefcase, ExternalLink, Video, GraduationCap, FileText, Play, Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { parseISO } from 'date-fns';
import { formatInTz } from '../../utils/dateFormatters';
import { useTimezone } from '../../hooks/useTimezone';
import { meetingService } from '../../services/meetingService';
import TranscriptViewerModal from './TranscriptViewerModal';
import RecordingPlayerModal from './RecordingPlayerModal';

interface MeetingDetailsModalProps {
  meeting: any;
  onClose: () => void;
}

const MeetingDetailsModal: React.FC<MeetingDetailsModalProps> = ({ meeting, onClose }) => {
  const tz = useTimezone();
  const navigate = useNavigate();
  const [showTranscript, setShowTranscript] = useState(false);
  const [showRecording, setShowRecording] = useState(false);

  const handleAICoach = () => {
    const action = meeting.meeting_status === 'attended' ? 'meeting-feedback' : 'meeting-prep';
    onClose();
    navigate(`/sales-coach?action=${action}&meetingId=${meeting.meeting_id}&leadId=${meeting.lead_id}`);
  };

  const getStatusStyle = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'attended':
        return 'bg-[#05E0D1]/10 text-[#05E0D1] border-[#05E0D1]/30';
      case 'missed':
        return 'bg-red-50 text-red-700 border-red-200';
      case 'upcoming':
        return 'bg-[#1875FF]/10 text-[#1875FF] border-[#1875FF]/30';
      case 'cancelled':
        return 'bg-gray-50 text-gray-700 border-gray-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const formatDate = (date: string) => {
    if (!date) return 'N/A';
    try {
      return formatInTz(parseISO(date), 'MMMM dd, yyyy', tz);
    } catch {
      return date;
    }
  };

  const renderIconLink = (url: string | undefined, icon: React.ReactNode, hoverColor: string, blueDefault = false) => {
    if (!url) return null;
    const href = url.startsWith('http') ? url : `https://${url}`;
    const baseColor = blueDefault ? 'bg-[#1875FF]/10 text-[#1875FF]' : 'bg-gray-100 text-gray-500';
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={`inline-flex items-center justify-center w-7 h-7 rounded-lg ${baseColor} ${hoverColor} transition-all hover:shadow-sm`}
      >
        {icon}
      </a>
    );
  };

  const meetingLink = meeting.meeting_link;
  const hasLink = meetingLink && meetingLink.trim() !== '';

  const isMeetingPast = (() => {
    if (!meeting.meeting_date) return false;
    try {
      const dateStr = meeting.meeting_date;
      const timeStr = meeting.meeting_time || '23:59';
      const timeParts = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
      if (!timeParts) return new Date(dateStr) < new Date();
      let hours = parseInt(timeParts[1], 10);
      const minutes = parseInt(timeParts[2], 10);
      const period = timeParts[3];
      if (period) {
        if (period.toUpperCase() === 'PM' && hours !== 12) hours += 12;
        if (period.toUpperCase() === 'AM' && hours === 12) hours = 0;
      }
      const meetingDate = parseISO(dateStr);
      meetingDate.setHours(hours, minutes, 0, 0);
      return meetingDate < new Date();
    } catch {
      return false;
    }
  })();

  const isJoinDisabled = !hasLink || isMeetingPast;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col shadow-2xl shadow-black/20"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#05E0D1]/20 bg-gradient-to-r from-[#05E0D1]/5 to-[#1875FF]/5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#05E0D1] to-[#1875FF] rounded-xl flex items-center justify-center shadow-lg">
              <Calendar className="text-white" size={18} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 font-display">Meeting Details</h3>
              <p className="text-xs text-gray-500 mt-0.5">{meeting.company_name ? `${meeting.company_name}${meeting.dm_full_name ? ` - ${meeting.dm_full_name}` : ''}` : 'View meeting information'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold border-2 ${getStatusStyle(meeting.meeting_status)}`}>
              {meeting.meeting_status || 'Unknown'}
            </span>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-white rounded-lg"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto settings-scrollbar bg-gray-50/50 p-6 space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-1">
                <Calendar size={14} className="text-[#1875FF]" />
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-widest">Date</span>
              </div>
              <div className="font-semibold text-gray-900 text-sm tabular-nums">{formatDate(meeting.meeting_date)}</div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-1">
                <Clock size={14} className="text-[#05E0D1]" />
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-widest">Time</span>
              </div>
              <div className="font-semibold text-gray-900 text-sm tabular-nums">{meeting.meeting_time || 'N/A'}</div>
            </div>
            {isJoinDisabled ? (
              <div className="bg-gray-100 rounded-xl border border-gray-200 p-4 shadow-sm flex flex-col justify-center items-center gap-2 opacity-50 cursor-not-allowed">
                <div className="flex items-center gap-2">
                  <Video size={16} className="text-gray-400" />
                  <span className="text-sm font-bold text-gray-400">
                    {!hasLink ? 'No Link Available' : 'Meeting Ended'}
                  </span>
                </div>
              </div>
            ) : (
              <a
                href={meetingLink.startsWith('http') ? meetingLink : `https://${meetingLink}`}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-gradient-to-r from-[#05E0D1] to-[#1875FF] rounded-xl p-4 shadow-sm hover:shadow-lg transition-all flex flex-col justify-center items-center gap-2 cursor-pointer"
              >
                <div className="flex items-center gap-2">
                  <Video size={16} className="text-white" />
                  <span className="text-sm font-bold text-white">Click To Join</span>
                  <ExternalLink size={14} className="text-white/70 group-hover:text-white transition-colors" />
                </div>
              </a>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <h4 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2 uppercase tracking-widest font-display">
                <Building size={16} className="text-[#1875FF]" />
                Company
              </h4>
              <div className="space-y-4">
                <div>
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Name</span>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="font-medium text-gray-900">{meeting.company_name || 'N/A'}</span>
                    {renderIconLink(meeting.company_website, <Globe size={14} />, 'hover:bg-[#1875FF]/10 hover:text-[#1875FF]', true)}
                    {renderIconLink(meeting.company_linkedin, <Linkedin size={14} />, 'hover:bg-[#0A66C2]/10 hover:text-[#0A66C2]', true)}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Size</span>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <Users size={14} className="text-[#05E0D1]" />
                      <span className="font-medium text-gray-900 text-sm">{meeting.employee_headcount || 'N/A'}</span>
                    </div>
                  </div>
                  <div>
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Type</span>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <Briefcase size={14} className="text-[#05E0D1]" />
                      <span className="font-medium text-gray-900 text-sm">{meeting.company_type || 'N/A'}</span>
                    </div>
                  </div>
                </div>
                <div>
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Industry</span>
                  <div className="mt-0.5">
                    <span className="font-medium text-gray-900 text-sm">
                      {[meeting.industry_1, meeting.industry_2].filter(Boolean).join(', ') || 'N/A'}
                    </span>
                  </div>
                </div>
                {meeting.products_services && (
                  <div>
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Products & Services</span>
                    <div className="font-medium text-gray-700 text-sm mt-0.5 break-words leading-relaxed">{meeting.products_services}</div>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <h4 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2 uppercase tracking-widest font-display">
                <User size={16} className="text-[#05E0D1]" />
                Person
              </h4>
              <div className="space-y-4">
                <div>
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Name</span>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="font-medium text-gray-900">{meeting.dm_full_name || 'N/A'}</span>
                    {renderIconLink(meeting.dm_linkedin, <Linkedin size={14} />, 'hover:bg-[#0A66C2]/10 hover:text-[#0A66C2]', true)}
                  </div>
                </div>
                <div>
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Title</span>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <Briefcase size={14} className="text-[#05E0D1]" />
                    <span className="font-medium text-gray-900 text-sm">{meeting.dm_title || 'N/A'}</span>
                  </div>
                </div>
                <div>
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Phone</span>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <Phone size={14} className="text-[#05E0D1]" />
                    <span className="font-medium text-gray-900 text-sm">{meeting.dm_mobile_1 || 'N/A'}</span>
                  </div>
                </div>
                <div>
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Email</span>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <Mail size={14} className="text-[#05E0D1]" />
                    <span className="font-medium text-gray-900 text-sm">{meeting.dm_email_1 || 'N/A'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {meeting.ovrall_convo_summary && (
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2 uppercase tracking-widest font-display">
                <MessageCircle size={16} className="text-[#05E0D1]" />
                Conversation Summary
              </h4>
              <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                {meeting.ovrall_convo_summary}
              </div>
            </div>
          )}

          {(() => {
            const ts = meeting.transcript_status;
            const tx = meeting.transcript;
            const hasJsonTranscript = tx && !tx.startsWith('http') && tx.startsWith('[');
            const segments = hasJsonTranscript ? meetingService.parseTranscript(tx) : [];

            if (ts === 'processing') {
              return (
                <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                  <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2 uppercase tracking-widest font-display">
                    <FileText size={16} className="text-[#1875FF]" />
                    Meeting Transcript
                  </h4>
                  <div className="flex items-center gap-2 text-amber-500 text-sm font-medium">
                    <Loader2 size={16} className="animate-spin" />
                    Transcript is being processed...
                  </div>
                </div>
              );
            }

            if (ts === 'failed') {
              return (
                <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                  <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2 uppercase tracking-widest font-display">
                    <FileText size={16} className="text-[#1875FF]" />
                    Meeting Transcript
                  </h4>
                  <div className="flex items-center gap-2 text-red-500 text-sm font-medium">
                    <AlertCircle size={16} />
                    Transcript processing failed
                  </div>
                </div>
              );
            }

            if (hasJsonTranscript && segments.length > 0) {
              const preview = segments.slice(0, 3);
              return (
                <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                  <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2 uppercase tracking-widest font-display">
                    <FileText size={16} className="text-[#1875FF]" />
                    Meeting Transcript
                  </h4>
                  <div className="space-y-2 mb-4">
                    {preview.map((seg, i) => (
                      <div key={i} className="text-sm">
                        <span className="font-semibold text-gray-800">{seg.speaker}</span>
                        <span className="text-gray-400 text-xs ml-1.5">{seg.timestamp}</span>
                        <p className="text-gray-600 mt-0.5 line-clamp-2">{seg.text}</p>
                      </div>
                    ))}
                    {segments.length > 3 && (
                      <p className="text-xs text-gray-400">...and {segments.length - 3} more segments</p>
                    )}
                  </div>
                  <button
                    onClick={() => setShowTranscript(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-[#1875FF]/10 text-[#1875FF] rounded-lg font-semibold text-sm hover:bg-[#1875FF]/20 transition-all"
                  >
                    <FileText size={14} />
                    View Full Transcript
                  </button>
                </div>
              );
            }

            return null;
          })()}

          {(() => {
            const rec = meeting.recording;
            const rs = meeting.recall_status;

            if (rs === 'recording') {
              return (
                <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                  <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2 uppercase tracking-widest font-display">
                    <Video size={16} className="text-[#05E0D1]" />
                    Meeting Recording
                  </h4>
                  <div className="flex items-center gap-2 text-red-500 text-sm font-medium">
                    <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
                    Recording in progress...
                  </div>
                </div>
              );
            }

            if (rec === 'available') {
              return (
                <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                  <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2 uppercase tracking-widest font-display">
                    <Video size={16} className="text-[#05E0D1]" />
                    Meeting Recording
                  </h4>
                  <button
                    onClick={() => setShowRecording(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-[#05E0D1]/10 text-[#05E0D1] rounded-lg font-semibold text-sm hover:bg-[#05E0D1]/20 transition-all"
                  >
                    <Play size={14} />
                    Watch Recording
                  </button>
                </div>
              );
            }

            if (rec === 'processing') {
              return (
                <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                  <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2 uppercase tracking-widest font-display">
                    <Video size={16} className="text-[#05E0D1]" />
                    Meeting Recording
                  </h4>
                  <div className="flex items-center gap-2 text-gray-400 text-sm font-medium">
                    <Loader2 size={16} className="animate-spin" />
                    Processing recording…
                  </div>
                </div>
              );
            }

            return null;
          })()}

          {meeting.lead_id && (
            <button
              onClick={handleAICoach}
              className="w-full flex items-center justify-center gap-2.5 p-4 bg-gradient-to-r from-[#1875FF] to-[#05E0D1] text-white rounded-xl font-semibold text-sm hover:shadow-lg hover:shadow-[#1875FF]/20 transition-all"
            >
              <GraduationCap size={18} />
              {meeting.meeting_status === 'attended' ? 'Review Meeting with AI Coach' : 'Prepare with AI Coach'}
            </button>
          )}
        </div>
      </div>

      {showTranscript && (
        <TranscriptViewerModal
          isOpen={showTranscript}
          onClose={() => setShowTranscript(false)}
          segments={meetingService.parseTranscript(meeting.transcript || '')}
          salesPerson={meeting.sales_person}
          leadName={meeting.dm_full_name}
          companyName={meeting.company_name}
          meetingDate={meeting.meeting_date ? (() => { try { return formatInTz(parseISO(meeting.meeting_date), 'MMM dd, yyyy', tz); } catch { return meeting.meeting_date; } })() : undefined}
        />
      )}

      {showRecording && (
        <RecordingPlayerModal
          isOpen={showRecording}
          onClose={() => setShowRecording(false)}
          meetingId={meeting.meeting_id}
          companyName={meeting.company_name}
          leadName={meeting.dm_full_name}
          meetingDate={meeting.meeting_date ? (() => { try { return formatInTz(parseISO(meeting.meeting_date), 'MMM dd, yyyy', tz); } catch { return meeting.meeting_date; } })() : undefined}
        />
      )}
    </div>,
    document.body
  );
};

export default MeetingDetailsModal;
